import * as z from 'zod';

const optionalNumberField = (fieldLabel: string) =>
  z.preprocess(
    (val) => {
      if (val === '' || val === null || val === undefined) return undefined;
      const num = Number(val);
      return isNaN(num) ? undefined : num;
    },
    z
      .number({ required_error: `${fieldLabel} is required`, invalid_type_error: `${fieldLabel} is required` })
      .min(0, { message: `${fieldLabel} must be 0 or greater` })
  );

export const budgetMemberRoles = ['owner', 'validation', 'staff'] as const;
export type BudgetMemberRole = (typeof budgetMemberRoles)[number];

export const budgetMemberEditRoles = [...budgetMemberRoles, 'remove_access'] as const;
export type BudgetMemberEditRole = (typeof budgetMemberEditRoles)[number];

export const budgetMemberSchema = z.object({
  memberId: z.string().optional().default(''),
  userId: z.string(),
  email: z.string().email({ message: 'Invalid email address' }),
  memberName: z.string().optional().default(''),
  profilePicUrl: z.string().nullable().optional().default(null),
  type: z.string().min(1, { message: 'Type is required' }),
  oreHours: optionalNumberField('Ore hours'),
  hourlyRate: z.preprocess(
    (val) => {
      if (val === '' || val === null || val === undefined) return 0;
      const num = Number(val);
      return isNaN(num) ? 0 : num;
    },
    z.number().min(0, { message: 'Hourly rate must be 0 or greater' })
  ),
  role: z.enum(budgetMemberRoles)
});

export type BudgetMemberFormValues = z.infer<typeof budgetMemberSchema>;

export const budgetFormSchema = z.object({
  budgetName: z.string().min(1, { message: 'Name is required' }),
  budgetType: z.string().min(1, { message: 'Type is required' }),
  budgetIcon: z.string().min(1, { message: 'Budget icon is required' }),
  budgetColor: z.string(),
  description: z.string().min(1, { message: 'Description is required' }),
  startDate: z.date(),
  endDate: z.date(),
  period: z.string(),
  currency: z.string(),
  autoRenewal: z.boolean(),
  bmev: z.preprocess(
    (val) => {
      if (val === '' || val === null || val === undefined) return undefined;
      const num = Number(val);
      return isNaN(num) ? undefined : num;
    },
    z.number({ required_error: 'BMEV is required', invalid_type_error: 'BMEV is required' }).min(1, {
      message: 'BMEV must be greater than 0'
    })
  ),
  bmevThreshold: z.number(),
  btrt: z.preprocess(
    (val) => {
      if (val === '' || val === null || val === undefined) return undefined;
      const num = Number(val);
      return isNaN(num) ? undefined : num;
    },
    z.number({ required_error: 'BTRT is required', invalid_type_error: 'BTRT is required' }).min(1, {
      message: 'BTRT must be greater than 0'
    })
  ),
  btrtThreshold: z.number(),
  icon_id: z.string(),
  members: z.array(budgetMemberSchema).min(1, { message: 'At least one member is required' }),
  inviteEmail: z.string()
});

export type BudgetFormValues = z.infer<typeof budgetFormSchema>;

export const budgetStep1FieldNames = [
  'budgetName',
  'budgetType',
  'budgetIcon',
  'budgetColor',
  'description',
  'startDate',
  'endDate',
  'period',
  'currency',
  'autoRenewal',
  'bmev',
  'bmevThreshold',
  'btrt',
  'btrtThreshold',
  'icon_id'
] as const satisfies Readonly<(keyof BudgetFormValues)[]>;

export const getMembersStepFieldNames = (memberCount: number): string[] => {
  const fields: string[] = [];

  for (let index = 0; index < memberCount; index++) {
    fields.push(`members.${index}.type`, `members.${index}.oreHours`);
  }

  return fields;
};

export const getInvitableMembers = (members: BudgetMemberFormValues[]): BudgetMemberFormValues[] =>
  members.filter((member) => member.role !== 'owner');

export const getOwnerMember = (members: BudgetMemberFormValues[]): BudgetMemberFormValues | undefined =>
  members.find((member) => member.role === 'owner');

export const toApiMemberType = (type: string): 'INTERNAL_EMPLOYEE' | 'EXTERNAL_COLLABORATOR' =>
  type.toUpperCase() as 'INTERNAL_EMPLOYEE' | 'EXTERNAL_COLLABORATOR';

const budgetMemberEditSchema = budgetMemberSchema
  .extend({
    role: z.enum(budgetMemberEditRoles),
    type: z.string()
  })
  .superRefine((member, ctx) => {
    if (member.role === 'remove_access') {
      return;
    }

    if (!member.type || member.type.length < 1) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['type'], message: 'Type is required' });
    }

    const oreHours = member.oreHours;
    if (oreHours == null || oreHours === undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['oreHours'], message: 'Ore hours is required' });
      return;
    }

    const num = Number(oreHours);
    if (isNaN(num) || num < 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['oreHours'], message: 'Ore hours must be 0 or greater' });
    }
  });

export const budgetMembersEditFormSchema = z.object({
  members: z.array(budgetMemberEditSchema).min(1, { message: 'At least one member is required' })
});

export type BudgetMemberEditFormValues = z.infer<typeof budgetMemberEditSchema>;
export type BudgetMembersEditFormValues = z.infer<typeof budgetMembersEditFormSchema>;

export const getMembersEditStepFieldNames = (members: Array<{ role?: string }>): string[] => {
  const fields: string[] = [];

  members.forEach((member, index) => {
    if (member.role === 'remove_access') {
      return;
    }

    fields.push(`members.${index}.type`, `members.${index}.oreHours`);
  });

  return fields;
};

export type BudgetMembersListFormValues = {
  members: BudgetMemberEditFormValues[];
};
